import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PencilLine } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/toast/CustomToast";
import httpClient from "@/config/axios";
import type { NurseMonthlyLimitListResponse } from "@/api/nurses";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import {
  invalidateNurseCaches,
  patchNurseNightLimitsInListCaches,
} from "@/services/nurse/nurseCacheInvalidation";
import {
  getInfeasibilityFromError,
  type Infeasibility,
} from "@/types/roster-infeasibility";

const NIGHT_LIMIT_MAX = 15;

type NightKind = "fixed" | "max" | "clear";

interface NightBulkApplyPayload {
  group_id: string;
  year: number;
  month: number;
  kind: NightKind;
  value: number | null;
}

interface NightBulkHeaderControlProps {
  groupId: string;
  year: number;
  month: number;
}

/**
 * "나이트 개수" 컬럼 헤더에 마우스를 올리면 나타나는 연필 버튼.
 * 클릭하면 고정(n_exact)/최대(n_max)를 적용하거나 나이트 제한을 초기화한다.
 * 현재 병동/월의 야간 가능 근무자 전체에 일괄 적용한다
 * (POST /nurses/monthly-limits/night-bulk). 성공 시 전체 새로고침,
 * 검증 실패(422) 시 조합된 _ko 메시지 노출.
 */
export default function NightBulkHeaderControl({
  groupId,
  year,
  month,
}: NightBulkHeaderControlProps) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<NightKind>("fixed");
  const [value, setValue] = useState<number | null>(null);
  const [infeasibility, setInfeasibility] = useState<Infeasibility | null>(
    null
  );
  const [isInfeasibilityDialogOpen, setIsInfeasibilityDialogOpen] =
    useState(false);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const clearInfeasibilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const queryClient = useQueryClient();

  const showInfeasibilityDialog = useCallback((next: Infeasibility) => {
    if (clearInfeasibilityTimerRef.current) {
      clearTimeout(clearInfeasibilityTimerRef.current);
      clearInfeasibilityTimerRef.current = null;
    }
    setInfeasibility(next);
    setIsInfeasibilityDialogOpen(true);
  }, []);

  const closeInfeasibilityDialog = useCallback(() => {
    setIsInfeasibilityDialogOpen(false);
    if (clearInfeasibilityTimerRef.current) {
      clearTimeout(clearInfeasibilityTimerRef.current);
    }
    clearInfeasibilityTimerRef.current = setTimeout(() => {
      setInfeasibility(null);
      clearInfeasibilityTimerRef.current = null;
    }, 220);
  }, []);

  useEffect(() => {
    return () => {
      if (clearInfeasibilityTimerRef.current) {
        clearTimeout(clearInfeasibilityTimerRef.current);
      }
    };
  }, []);

  const { mutate, isPending } = useMutation<
    NurseMonthlyLimitListResponse | null,
    Error,
    NightBulkApplyPayload
  >({
    mutationFn: async (payload) => {
      try {
        return await httpClient.post<NurseMonthlyLimitListResponse>(
          "/nurses/monthly-limits/night-bulk",
          payload
        );
      } catch (error) {
        const nextInfeasibility = getInfeasibilityFromError(error);
        if (nextInfeasibility?.severity === "blocking") {
          showInfeasibilityDialog(nextInfeasibility);
          return null;
        }
        throw error;
      }
    },
    onSuccess: async (response) => {
      if (!response) return;

      patchNurseNightLimitsInListCaches(queryClient, response.items);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.monthlyLimitsScope(groupId),
      });
      await Promise.all([
        invalidateNurseCaches(queryClient, {
          listGroupId: groupId,
          memberGroupId: groupId,
          year,
          month,
        }),
        invalidateNurseCaches(queryClient, {
          listGroupId: "",
          memberGroupId: groupId,
          year,
          month,
        }),
      ]);
      setOpen(false);
      toast.success(
        kind === "clear"
          ? "야간 가능 근무자 전체의 나이트 설정을 초기화했습니다."
          : `야간 가능 근무자 전체에 나이트 ${value}개 ${
              kind === "fixed" ? "고정" : "최대"
            }을 적용했습니다.`
      );
    },
    onError: (err) => {
      toast.error(err?.message || "일괄 적용에 실패했습니다.");
    },
  });

  const handleSave = () => {
    if (!groupId) {
      toast.error("병동을 먼저 선택해주세요.");
      return;
    }
    if (kind === "clear") {
      setIsClearConfirmOpen(true);
      return;
    }
    if (value == null) {
      toast.error("나이트 개수를 입력해주세요.");
      return;
    }
    mutate({ group_id: groupId, year, month, kind, value });
  };

  const handleClearConfirm = () => {
    setIsClearConfirmOpen(false);
    mutate({ group_id: groupId, year, month, kind: "clear", value: null });
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="나이트 개수 일괄 설정"
            title="나이트 개수 일괄 설정"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-row-hover hover:text-sig focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30"
          >
            <PencilLine aria-hidden="true" className="size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-80 border-border bg-card p-4 font-pretendard text-foreground shadow-[0_1px_2px_rgba(15,23,42,0.06)]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-1 border-b border-border pb-3">
            <p className="text-sm font-semibold">나이트 개수 일괄 설정</p>
            <p className="break-keep text-xs leading-5 text-muted-foreground">
              {year}년 {month}월 · 선택한 병동의 야간 가능 근무자 전체에
              적용합니다.
            </p>
          </div>
          <fieldset className="mt-3 space-y-2">
            <legend className="text-xs font-semibold text-foreground">
              적용 방식
            </legend>
            {(["fixed", "max", "clear"] as NightKind[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setKind(k);
                  if (k === "clear") setValue(null);
                }}
                disabled={isPending}
                aria-pressed={kind === k}
                className={`flex w-full items-start gap-2 rounded-md border p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30 ${
                  kind === k
                    ? "border-sig bg-sig/8 text-foreground"
                    : "border-border bg-card text-muted-foreground hover:bg-light-muted"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`mt-0.5 size-3 shrink-0 rounded-full border-4 ${
                    kind === k
                      ? "border-sig bg-card"
                      : "border-muted-foreground/40 bg-card"
                  }`}
                />
                <span className="min-w-0">
                  <span className="block text-xs font-semibold">
                    {k === "fixed"
                      ? "고정 횟수"
                      : k === "max"
                        ? "최대 횟수"
                        : "나이트 설정 초기화"}
                  </span>
                  <span className="mt-0.5 block break-keep text-[11px] leading-4 text-muted-foreground">
                    {k === "fixed"
                      ? "입력한 횟수만 배정"
                      : k === "max"
                        ? "입력한 횟수 이하로 배정"
                        : "고정·최대 나이트 개수 설정을 모두 지움"}
                  </span>
                </span>
              </button>
            ))}
          </fieldset>
          <div className="mt-3 space-y-1.5">
            <label
              htmlFor="night-bulk-count"
              className="text-xs font-semibold text-foreground"
            >
              {kind === "clear" ? "횟수" : "횟수 (0–15)"}
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="night-bulk-count"
                name="night-bulk-count"
                type="number"
                min={0}
                max={NIGHT_LIMIT_MAX}
                value={value ?? ""}
                placeholder={kind === "clear" ? "초기화 시 입력 불필요" : "예: 4"}
                disabled={isPending || kind === "clear"}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  if (v === "") {
                    setValue(null);
                    return;
                  }
                  if (!/^\d+$/.test(v)) return;
                  const n = Number(v);
                  if (n > NIGHT_LIMIT_MAX) {
                    toast.error(
                      `나이트 개수는 ${NIGHT_LIMIT_MAX}개 이하로 설정할 수 있습니다.`
                    );
                    return;
                  }
                  setValue(n);
                }}
                className="h-9 text-center tabular-nums"
              />
              <span className="shrink-0 text-sm text-muted-foreground">개</span>
            </div>
          </div>
          <p className="mt-3 break-keep rounded-md bg-light-muted px-2.5 py-2 text-[11px] leading-4 text-muted-foreground">
            {kind === "clear"
              ? "나이트 고정·최대 설정만 지우며, 다른 근무 종류의 월별 제한은 유지합니다."
              : "기존 나이트 설정을 선택한 방식과 횟수로 덮어씁니다. 개별 해제는 각 근무자 행에서 ‘없음’을 선택하세요."}
          </p>
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={isPending}
              className="h-9 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-light-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30 disabled:opacity-50"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isPending || (kind !== "clear" && value == null)}
              className={`h-9 rounded-md px-3 text-sm font-semibold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50 ${
                kind === "clear"
                  ? "bg-destructive hover:bg-destructive/90 focus-visible:ring-destructive/30"
                  : "bg-sig hover:bg-sig/90 focus-visible:ring-sig/30"
              }`}
            >
              {isPending ? "적용 중…" : kind === "clear" ? "전체 초기화" : "전체적용"}
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={isClearConfirmOpen} onOpenChange={setIsClearConfirmOpen}>
        <DialogContent className="w-[min(420px,calc(100vw-40px))] gap-0 rounded-xl border-border bg-card p-0 font-pretendard shadow-[0_24px_80px_rgba(15,23,42,0.22)]">
          <div className="px-5 py-5">
            <DialogTitle className="text-base font-semibold text-foreground">
              나이트 설정을 초기화할까요?
            </DialogTitle>
            <DialogDescription className="mt-2 break-keep text-sm leading-6 text-muted-foreground">
              {year}년 {month}월 야간 가능 근무자 전체의 고정·최대 나이트 개수를 지웁니다.
              데이·이브닝·오프 제한은 유지됩니다.
            </DialogDescription>
          </div>
          <DialogFooter className="border-t border-border px-5 py-3">
            <button
              type="button"
              onClick={() => setIsClearConfirmOpen(false)}
              disabled={isPending}
              className="h-9 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-light-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30 disabled:opacity-50"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleClearConfirm}
              disabled={isPending}
              className="h-9 rounded-md bg-destructive px-3 text-sm font-semibold text-white transition-colors hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30 disabled:opacity-50"
            >
              {isPending ? "초기화 중…" : "초기화"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isInfeasibilityDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closeInfeasibilityDialog();
        }}
      >
        <DialogContent
          hideCloseButton
          overlayClassName="bg-black/45"
          className="w-[min(620px,calc(100vw-40px))] max-h-[calc(100dvh-80px)] gap-0 overflow-hidden rounded-xl border-0 bg-card p-0 font-pretendard shadow-[0_24px_80px_rgba(15,23,42,0.22)]"
        >
          <div className="border-b border-border px-6 py-5">
            <DialogTitle className="text-[20px] font-semibold text-foreground">
              나이트 개수 설정을 저장할 수 없습니다
            </DialogTitle>
            <DialogDescription className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
              서버 오류가 아니라 현재 나이트 개수 설정 조건이 맞지 않는
              상태입니다. 내용을 확인하고 값을 조정해주세요.
            </DialogDescription>
          </div>

          {infeasibility && (
            <div className="min-h-0 overflow-auto px-6 py-5">
              <p className="mb-4 whitespace-pre-wrap rounded-lg bg-destructive/8 px-3 py-2 text-[14px] leading-relaxed text-destructive break-keep">
                {infeasibility.summary_message_ko}
              </p>

              {infeasibility.preflight_issues.length > 0 && (
                <div className="mb-4">
                  <p className="mb-2 text-[14px] font-semibold text-foreground">
                    발견된 문제 ({infeasibility.preflight_issues.length})
                  </p>
                  <ul className="max-h-[180px] space-y-2 overflow-y-auto rounded-lg border border-border p-3">
                    {infeasibility.preflight_issues.map((issue) => (
                      <li
                        key={`${issue.reason_code}-${issue.human_message_ko}`}
                        className="text-[13px] leading-relaxed text-muted-foreground"
                      >
                        {issue.human_message_ko}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {infeasibility.fix_suggestions_ko.length > 0 && (
                <div>
                  <p className="mb-2 text-[14px] font-semibold text-foreground">
                    다음 중 하나를 시도해보세요
                  </p>
                  <ul className="space-y-1 rounded-lg bg-light-muted p-3">
                    {infeasibility.fix_suggestions_ko.map(
                      (suggestion) => (
                        <li
                          key={suggestion}
                          className="text-[13px] leading-relaxed text-muted-foreground"
                        >
                          {suggestion}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={closeInfeasibilityDialog}
              className="h-10 rounded-md bg-sig px-6 text-[14px] font-semibold text-white transition hover:bg-sig/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30 disabled:opacity-50"
            >
              확인
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
